import { op, Operation } from "libs/transaction";
import { createAction } from "./action";
import { InvalidActionStateError } from "./utils/errors";
import { getPointer } from "libs/schema";
import { promiseAllKeyed } from "libs/promise-utils";
import { applyOperationsToTransaction } from "libs/operations";
import { authorizeUserHasAccessToMessage, authorizeUserCanUpdateMessage } from "./authorizers/messageAuthorizers";

/* -------------------------------------------------------------------------------------------------
 *  setMessageReactions
 * -------------------------------------------------------------------------------------------------
 */

type SetMessageReactionsProps = {
  messageId: string;
  userReactionIds: string[];
};

export const setMessageReactions = createAction<SetMessageReactionsProps>("message.setMessageReactions", {
  operations: async (props, context) => {
    const { messageId, userReactionIds } = props;

    const pointer = getPointer("message_reactions", messageId);

    return op.set(pointer, ["reactions", context.currentUserId], userReactionIds);
  },
  authorize: async (props, context) => {
    await authorizeUserHasAccessToMessage({ messageId: props.messageId, context });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  unscheduleMessageToBeSent
 * -------------------------------------------------------------------------------------------------
 */

type UnscheduleMessageToBeSentProps = {
  messageId: string;
};

export const unscheduleMessageToBeSent = createAction<UnscheduleMessageToBeSentProps>(
  "message.unscheduleMessageToBeSent",
  {
    operations: async (props, context) => {
      const { loader } = context.environment;

      const operations: Operation[] = [];

      const options = { includeSoftDeletes: true };

      const [[message], [draft]] = await Promise.all([
        loader.getRecord("message", props.messageId, options),
        loader.getRecord("draft", props.messageId, options),
      ]);

      if (!message) {
        throw new InvalidActionStateError("[unscheduleMessageToBeSent] Message not found", { errorData: { props } });
      } else if (message.deleted_at) {
        throw new InvalidActionStateError("[unscheduleMessageToBeSent] Message already deleted", {
          errorData: { message },
        });
      } else if (message.sent_at <= new Date().toISOString()) {
        throw new InvalidActionStateError("[unscheduleMessageToBeSent] Message already sent", {
          errorData: { message },
        });
      }

      if (!draft) {
        throw new InvalidActionStateError("[unscheduleMessageToBeSent] Draft not found", { errorData: { props } });
      } else if (!draft.deleted_at) {
        throw new InvalidActionStateError("[unscheduleMessageToBeSent] Draft already unsent", { errorData: { draft } });
      } else if (draft.is_edit) {
        throw new InvalidActionStateError("[unscheduleMessageToBeSent] Draft is an edit", { errorData: { draft } });
      }

      operations.push(
        op.update(getPointer("draft", props.messageId), {
          deleted_at: null,
          deleted_by_user_id: null,
        }),
        op.delete("message", message),
        op.delete("message_reactions", message),
      );

      if (!message.is_reply) {
        // TODO: @Sudeep - Revisit and check if the thread can/should be deleted here as well

        const {
          getMessages: [messages],
          getThreadGroupPermissions: [groupPermissions],
          getThreadSubscriptions: [subscriptions],
          getThreadTags: [threadTags],
          getThreadTimelineEntries: [timelineEntries],
          getThreadUserParticipants: [userParticipants],
          getThreadUserPermissions: [userPermissions],
        } = await promiseAllKeyed({
          getMessages: loader.getMessages({ thread_id: message.thread_id }),
          getThreadGroupPermissions: loader.getThreadGroupPermissions({ thread_id: message.thread_id }),
          getThreadSubscriptions: loader.getThreadSubscriptions({ thread_id: message.thread_id }),
          getThreadTags: loader.getThreadTags({ thread_id: message.thread_id }),
          getThreadTimelineEntries: loader.getThreadTimelineEntries({ thread_id: message.thread_id }),
          getThreadUserParticipants: loader.getThreadUserParticipants({ thread_id: message.thread_id }),
          getThreadUserPermissions: loader.getThreadUserPermissions({ thread_id: message.thread_id }),
        });

        if (messages.length > 1) {
          throw new InvalidActionStateError("[unscheduleMessageToBeSent] Thread has more than one message", {
            errorData: { threadId: message.thread_id },
          });
        }

        applyOperationsToTransaction(
          operations,
          groupPermissions.map((r) => op.delete("thread_group_permission", r)),
          subscriptions.map((r) => op.delete("thread_subscription", r)),
          threadTags.map((r) => op.delete("thread_tag", r)),
          timelineEntries.map((r) => op.delete("thread_timeline", r)),
          userParticipants.map((r) => op.delete("thread_user_participant", r)),
          userPermissions.map((r) => op.delete("thread_user_permission", r)),
        );
      }

      if (draft.branched_from_thread_id) {
        operations.push(
          op.update(
            getPointer("thread_timeline", {
              entry_id: message.id,
              thread_id: draft.branched_from_thread_id,
            }),
            { deleted_at: null, deleted_by_user_id: null },
          ),
        );
      }

      return operations;
    },
    authorize: async (props, context) => {
      await authorizeUserCanUpdateMessage({ messageId: props.messageId, context });
    },
  },
);

export const actions = {
  setMessageReactions,
  unscheduleMessageToBeSent,
};
